/**
 * Jovii — THE ENDLESS RAIL'S ARITHMETIC.
 *
 * Every rail now runs to the end of its category and then loops back to its
 * own beginning (the owner, 2026-08-17, replacing the way-back tile: "an
 * infinitely loading rail for every rail … if we finish everything in the
 * category, we loop back to the beginning"). The DOM must not pay for that:
 * a Documentary shelf is 2,644 titles deep (measured 2026-08-17 — Western
 * 414, Sport 618, Game-Show 284; ungenred `top` still answers full pages at
 * skip 3000), and 2,644 posters — let alone two copies of them — is not a
 * row anybody can scroll on a television SoC.
 *
 * So the deep rails are a WINDOWED VIRTUAL RING: the track renders a bounded
 * slice of an endless tape of positions, the tape reads `items[j % count]`
 * once the category is exhausted, and a spacer of exactly the dropped
 * columns' width stands in for everything recycled off the left. Geometry is
 * exact by construction — every rail column is `--col` wide with a `--gap`
 * after it — so recycling and restoring never move a pixel that is on
 * screen, and THERE IS NO SEAM: walking right simply never ends, because the
 * next position always exists and always has an item. What must stay small
 * is only the numbers, which is what `shedWraps` is for.
 *
 * The short static rails (Continue watching, Recently opened, more-like)
 * loop by the other mechanism — the whole sequence rendered a bounded number
 * of times and the scroller teleported back a stride during a settled frame
 * (`copiesFor` / `teleportWraps`); their sequences are ≤ ~20 cards, so
 * copies are cheaper than a ring's machinery there.
 *
 * All of it is arithmetic, so all of it lives here, in a module with no DOM
 * in it, tested in `rail-ring.test.ts` — "does the window drift?" is a
 * question about numbers.
 */

/* ────────────────────────────────────────────────────────────────────────────
 * The numbers
 * ──────────────────────────────────────────────────────────────────────────── */

/** An untouched rail renders this many positions — the old LazyRail WINDOW. */
export const RING_WINDOW = 10;

/** One growth of the window per approach — the old LazyRail STEP. */
export const RING_STEP = 20;

/**
 * The most positions a ring rail ever renders at once. Eighty is four growth
 * steps: at the narrowest column (135.75px on a 390 phone, plus the 12px gap)
 * that is an ~11,800px window around the viewport, so recycling only ever
 * touches tiles thousands of pixels off the glass — and at ~13 DOM nodes a
 * poster the whole rail holds near ~1,100 nodes at ANY depth, which is the
 * bound the old 60-cap existed to approximate.
 */
export const RING_CAP = 80;

/**
 * How close to the track's right end the scroll may come before the window
 * grows (and, when the tape is short, the catalogue is asked for more).
 * 700px is a hair over the old ghost-sentinel's 600px margin: the next step
 * is rendered before the reader reaches the shimmer, not after.
 */
export const NEAR_END_PX = 700;

/** How close to the spacer's end the scroll may come before tiles restore. */
export const NEAR_SPACER_PX = 700;

/**
 * How long the scroller must be quiet before the ring sheds whole wraps or a
 * copies-loop teleports its stride. Longer than a scroll event gap, shorter
 * than the 1400ms tile dwell — the correction must land before a trailer
 * starts, so the trailer starts in the tile that will stay on the glass.
 */
export const LOOP_SETTLE_MS = 160;

/**
 * Fling runway for the copies loop: how much scrollable road must lie beyond
 * one stride after a teleport, so a momentum fling crosses the wrap inside
 * real content and the correction can wait for the fling to settle. A hard
 * phone fling measured on the rails travels ~2–4,000px; 4,000 covers it.
 */
export const LOOP_RUNWAY_PX = 4000;

/** The most times a copies-loop repeats its sequence, however short it is. */
export const MAX_COPIES = 6;

/**
 * After a rejected `more()`, how long before the rail asks again. Long enough
 * not to hammer an endpoint that just failed, short enough that a network
 * blip reads as a pause and not a dead rail.
 */
export const MORE_RETRY_MS = 3000;

/**
 * True when the tail ghost is comfortably off the glass, so unmounting it
 * cannot move anything a viewer can see. The margin comfortably exceeds the
 * widest column plus the track's end padding: a ghost within it might be
 * showing, and a ghost that is showing must NOT unmount — removing it
 * shrinks scrollWidth, and a viewer parked at the very end gets their
 * scrollLeft clamped backward by a whole column (measured: a ~184–236px hop
 * the moment a fetch rejected). The ghost outlives the failure instead, and
 * leaves only once it is off screen or replaced by real tiles.
 */
export function ghostHidden(
  scrollLeft: number,
  clientWidth: number,
  scrollWidth: number,
  margin = 500
): boolean {
  return scrollLeft + clientWidth < scrollWidth - margin;
}

/* ────────────────────────────────────────────────────────────────────────────
 * The ring window
 * ──────────────────────────────────────────────────────────────────────────── */

export interface RingWindow {
  /** First rendered tape position. */
  lo: number;
  /** One past the last rendered tape position. */
  hi: number;
}

/** 1-based position within the sequence — the ranked rail's numeral, in any wrap. */
export function ordinalAt(j: number, count: number): number {
  if (count <= 0) return j + 1;
  return (j % count) + 1;
}

/** True when the viewport has come within `lookahead` of the track's end. */
export function nearEnd(
  scrollLeft: number,
  clientWidth: number,
  scrollWidth: number,
  lookahead: number = NEAR_END_PX
): boolean {
  return scrollLeft + clientWidth >= scrollWidth - lookahead;
}

/**
 * True when the viewport has come within `margin` of the spacer's right edge
 * — the cue to restore recycled tiles before their blank shows. `spacerEnd`
 * is the spacer's right edge in content coordinates (its viewport-right plus
 * the scroll), so the comparison is scroll-invariant.
 */
export function nearSpacer(scrollLeft: number, spacerEnd: number, margin: number = NEAR_SPACER_PX): boolean {
  return scrollLeft <= spacerEnd + margin;
}

export interface GrowPlan {
  lo: number;
  hi: number;
  /** The tape has run out and the category has not: fetch before growing. */
  ask: boolean;
}

/**
 * The window after one approach to the track's end.
 *
 * While the category is still being fetched the tape is exactly `count`
 * items long, so a window that has reached it cannot grow — it must ASK
 * (the caller runs the rail's `more()` and comes back). Once exhausted the
 * tape is endless (`items[j % count]`) and the window simply advances.
 * Growth recycles from the left whatever exceeds the cap: `lo` rises with
 * `hi`, and the spacer widens by the same columns, so nothing on the glass
 * moves.
 *
 * `wrap` is THE LOOP'S LICENCE, granted only when one whole wrap of the
 * sequence overflows the viewport (Rail measures it). Looping hides its seam
 * beyond the glass; a rail whose entire content FITS has nowhere to hide one,
 * so repeating positions would set the same poster beside itself in plain
 * view — measured on the Top 10 at 2880px wide, ranks 1–10 drew and then #1
 * and #2 drew again right next to them. Without the licence an exhausted
 * window parks exactly at `count`: complete, single, still.
 */
export function growPlan(
  win: RingWindow,
  count: number,
  exhausted: boolean,
  wrap: boolean = true,
  step: number = RING_STEP,
  cap: number = RING_CAP
): GrowPlan {
  if (count <= 0) return { lo: win.lo, hi: win.hi, ask: !exhausted };
  const edge = !exhausted && win.hi >= count;
  if (edge) return { lo: win.lo, hi: win.hi, ask: true };
  /* Without the licence growth stalls at `count` but NEVER retreats: a
     window that wrapped while the licence held is folded back by the rail's
     own `refit`, in a settled frame — shrinking it here would unmount tail
     tiles mid-scroll and clamp the viewer's scrollLeft backward. */
  const hi = exhausted && wrap ? win.hi + step : Math.max(win.hi, Math.min(win.hi + step, count));
  const lo = Math.max(win.lo, hi - cap);
  return { lo, hi, ask: false };
}

/**
 * The window after one approach to the spacer: restore up to a step of
 * recycled tiles on the left (never past `floor` — the columns the spacer
 * actually holds, which after a shed is less than `lo`) and trim the far
 * right back to the cap. The trim is safe by construction: this only runs
 * when the viewport is near the window's LEFT edge, and the cap keeps the
 * right edge thousands of pixels beyond the glass.
 */
export function restorePlan(
  win: RingWindow,
  floor: number,
  step: number = RING_STEP,
  cap: number = RING_CAP
): RingWindow {
  const back = Math.min(step, floor, win.lo);
  if (back <= 0) return win;
  const lo = win.lo - back;
  const hi = Math.min(win.hi, lo + cap);
  return { lo, hi };
}

/**
 * How many WHOLE wraps of the sequence the spacer holds — the shed that
 * keeps a looping rail's numbers (and its scrollLeft) from growing without
 * bound. Shedding `k * count` columns from the spacer and `k * count *
 * stride` pixels from the scroll in the same settled frame shows the same
 * items at the same glass positions, because the tape is periodic in exactly
 * `count`.
 */
export function shedWraps(spacerCols: number, count: number): number {
  if (count <= 0) return 0;
  return Math.floor(spacerCols / count);
}

/* ────────────────────────────────────────────────────────────────────────────
 * The copies loop, for the short static rails
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * How many copies of the sequence a static rail renders once it loops. At
 * least two (one on the glass, one to walk into), and enough that a full
 * fling from just past the first stride still lands inside content — the
 * teleport back happens at settle, so the road ahead must outlast the fling.
 * Capped, so a three-card rail cannot multiply itself into absurdity.
 */
export function copiesFor(
  clientWidth: number,
  seqWidth: number,
  runway: number = LOOP_RUNWAY_PX,
  max: number = MAX_COPIES
): number {
  if (seqWidth <= 0) return 1;
  const wanted = 1 + Math.ceil((clientWidth + runway) / seqWidth);
  return Math.min(max, Math.max(2, wanted));
}

/**
 * How many whole strides the settled scroll sits past the first copy — the
 * teleport that brings it back to an identical frame. Zero while the scroll
 * is still inside the first copy, where nothing needs doing.
 */
export function teleportWraps(scrollLeft: number, stride: number): number {
  if (stride <= 0) return 0;
  return Math.floor(scrollLeft / stride);
}
